import { useAuthStore } from "~/stores/useAuthStore";
import { useCombatStore } from "~/stores/useCombatStore";
import { useToast } from "~/composables/useToast";
import { useLocale } from "~/composables/useLocale";
import { repairCombatLoop } from "~/composables/useCombatLoop";
import { isPokemonsDirty } from "~/stores/useInventoryStore";
import {
  flushDaycareProgressBeforeStandby,
  hydrateDaycareFromLocalProgressBackup,
} from "~/composables/useDaycareCrossTabSync";
import { nudgeDaycareAutoRefillIfNeeded } from "~/composables/useDaycareAutoRefill";

/** Bail d’exclusivité partagé entre fenêtres / onglets (même compte, même origine). */
const LEASE_STORAGE_KEY = "poke-idle-game-tab-lease";
const TAB_ID_SESSION_KEY = "poke-idle-tab-id";
const LEASE_TTL_MS = 5_000;
const HEARTBEAT_MS = 2_000;
const BLUR_GRACE_MS = 1_200;
const SECONDARY_TOAST_SESSION_KEY = "poke-idle-multi-tab-toast";

type TabLease = { id: string; ts: number };

const isLeader = ref(true);
let tabId = "";
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let blurTimer: ReturnType<typeof setTimeout> | null = null;
let bound = false;
let leadershipEvalInFlight = false;

/** Onglet / fenêtre autorisé à farmer, sauvegarder et pousser l’or. */
export function isGameTabLeader(): boolean {
  return isLeader.value;
}

export function useMultiTabGuard() {
  const { t } = useLocale();
  const { addToast } = useToast();

  function ensureTabId(): string {
    if (tabId) return tabId;
    if (typeof sessionStorage !== "undefined") {
      const existing = sessionStorage.getItem(TAB_ID_SESSION_KEY);
      if (existing) {
        tabId = existing;
        return tabId;
      }
      tabId =
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(TAB_ID_SESSION_KEY, tabId);
      return tabId;
    }
    tabId = `tab-${Date.now()}`;
    return tabId;
  }

  function readLease(): TabLease | null {
    if (typeof localStorage === "undefined") return null;
    try {
      const raw = localStorage.getItem(LEASE_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as TabLease;
      if (!parsed?.id || typeof parsed.ts !== "number") return null;
      return parsed;
    } catch {
      return null;
    }
  }

  function writeLease() {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(
      LEASE_STORAGE_KEY,
      JSON.stringify({ id: ensureTabId(), ts: Date.now() } satisfies TabLease),
    );
  }

  function isLeaseStale(lease: TabLease | null): boolean {
    if (!lease) return true;
    return Date.now() - lease.ts > LEASE_TTL_MS;
  }

  function showSecondaryToastOnce() {
    try {
      if (
        typeof sessionStorage !== "undefined" &&
        sessionStorage.getItem(SECONDARY_TOAST_SESSION_KEY)
      ) {
        return;
      }
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(SECONDARY_TOAST_SESSION_KEY, "1");
      }
    } catch {
      /* ignore */
    }
    addToast(
      t(
        "Une autre fenêtre Poke Idle est active : farm et sauvegardes sont en pause ici. Ferme l’autre fenêtre ou clique dedans pour reprendre.",
        "Another Poke Idle window is active: farming and saves are paused here. Close the other window or focus it to continue.",
      ),
      "⚠️",
      "warning",
      12_000,
    );
  }

  async function applyLeadership(next: boolean) {
    if (isLeader.value === next) return;

    const combat = useCombatStore();
    const auth = useAuthStore();

    if (!next) {
      combat.pauseForVisibility();
      if (auth.isAuthenticated && auth.gameLoaded) {
        try {
          await flushDaycareProgressBeforeStandby();
        } catch {
          /* best effort */
        }
        try {
          const { flushCombatGoldCredit } = await import('~/composables/useCombatGoldCredit');
          await flushCombatGoldCredit({ force: true, maxPasses: 6 });
        } catch {
          /* best effort */
        }
      }
      isLeader.value = false;
      showSecondaryToastOnce();
      return;
    }

    isLeader.value = true;
    if (auth.isAuthenticated && auth.gameLoaded) {
      const daycareMerged = hydrateDaycareFromLocalProgressBackup();
      if (daycareMerged) nudgeDaycareAutoRefillIfNeeded();
      if (daycareMerged || isPokemonsDirty()) {
        void auth
          .saveGameState(false, true, {
            pokemonsFirst: true,
            forcePokemons: true,
          })
          .catch(() => undefined);
      }
    }
    if (typeof document !== "undefined" && !document.hidden) {
      combat.resumeFromVisibility();
      repairCombatLoop();
    }
  }

  function evaluateLeadership() {
    if (typeof document === "undefined") return;
    if (leadershipEvalInFlight) return;
    leadershipEvalInFlight = true;
    try {
      ensureTabId();

      const lease = readLease();
      const stale = isLeaseStale(lease);
      const weOwn = lease?.id === tabId;

      if (document.hasFocus()) {
        if (!weOwn || stale) writeLease();
        void applyLeadership(true);
        return;
      }

      if (weOwn && !stale) {
        writeLease();
        void applyLeadership(true);
        return;
      }

      if (!stale && lease && lease.id !== tabId) {
        void applyLeadership(false);
        return;
      }

      if (stale) {
        writeLease();
        void applyLeadership(true);
      } else {
        void applyLeadership(false);
      }
    } finally {
      leadershipEvalInFlight = false;
    }
  }

  function onStorage(e: StorageEvent) {
    if (e.key !== LEASE_STORAGE_KEY) return;
    evaluateLeadership();
  }

  function onVisibilityChange() {
    if (blurTimer) {
      clearTimeout(blurTimer);
      blurTimer = null;
    }
    evaluateLeadership();
  }

  function onWindowFocus() {
    if (blurTimer) {
      clearTimeout(blurTimer);
      blurTimer = null;
    }
    evaluateLeadership();
  }

  function onWindowBlur() {
    if (typeof document === "undefined" || document.hidden) return;
    if (blurTimer) clearTimeout(blurTimer);
    blurTimer = setTimeout(() => {
      blurTimer = null;
      evaluateLeadership();
    }, BLUR_GRACE_MS);
  }

  function bindMultiTabGuard() {
    if (bound || typeof window === "undefined") return;
    bound = true;
    ensureTabId();
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onWindowFocus);
    window.addEventListener("blur", onWindowBlur);
    document.addEventListener("visibilitychange", onVisibilityChange);
    evaluateLeadership();
    if (!heartbeatTimer) {
      heartbeatTimer = setInterval(() => {
        if (typeof document === "undefined" || document.hidden) return;
        if (!isLeader.value && !document.hasFocus()) return;
        if (isLeader.value || document.hasFocus()) writeLease();
        evaluateLeadership();
      }, HEARTBEAT_MS);
    }
  }

  function unbindMultiTabGuard() {
    if (!bound || typeof window === "undefined") return;
    bound = false;
    window.removeEventListener("storage", onStorage);
    window.removeEventListener("focus", onWindowFocus);
    window.removeEventListener("blur", onWindowBlur);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    if (blurTimer) {
      clearTimeout(blurTimer);
      blurTimer = null;
    }
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
    isLeader.value = true;
  }

  return {
    isLeader,
    bindMultiTabGuard,
    unbindMultiTabGuard,
    evaluateLeadership,
  };
}
